import React, { useState, useEffect } from "react";
import Header from "../components/HomePage/header";
import Footer from "../components/HomePage/footer";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'
  const [loading, setLoading] = useState(false); // Track loading state
  const [timer, setTimer] = useState(null); // For the 5-minute countdown
  const [retryTimer, setRetryTimer] = useState(0); // For the 30-second button disable countdown

  // Countdown for 5-minute reset link validity
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    if (timer === 0 && messageType === "success") {
      setMessage("The reset link has expired. Please try again.");
      setMessageType("error");
    }
  }, [timer, messageType]);

  // Countdown for 30-second button disable
  useEffect(() => {
    if (retryTimer > 0) {
      const interval = setInterval(() => {
        setRetryTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [retryTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRetryTimer(30); // Disable button for 30 seconds

    try {
      const response = await fetch("/api/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === "User not found") {
          setMessage("Invalid username. Please check your email.");
        } else {
          setMessage("Failed to send reset email. Please try again.");
        }
        setMessageType("error");
      } else {
        setMessage(
          "Password reset email sent successfully. Please check your email."
        );
        setMessageType("success");
        setTimer(300); // Start the 5-minute countdown (300 seconds)
      }
    } catch (err) {
      setMessage("An error occurred. Please try again later.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white shadow-md rounded-lg p-6 max-w-md w-full">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
            Forgot Password
          </h2>
          <p className="text-gray-600 text-sm mb-6 text-center">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>

          {message && (
            <div
              className={`mb-4 px-4 py-2 rounded-lg text-white ${
                messageType === "success" ? "bg-green-400" : "bg-red-400"
              }`}
            >
              {message}
              {messageType === "success" && timer > 0 && (
                <p className="text-sm mt-2">
                  The reset link is valid for{" "}
                  <strong>
                    {Math.floor(timer / 60)}:
                    {timer % 60 < 10 ? `0${timer % 60}` : timer % 60}
                  </strong>
                  .
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button
              type="submit"
              className={`w-full text-white py-2 rounded-lg flex items-center justify-center transition duration-300 ${
                retryTimer > 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              disabled={loading || retryTimer > 0}
            >
              {loading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
              ) : retryTimer > 0 ? (
                `Retry in ${retryTimer}s`
              ) : (
                "Send Reset Email"
              )}
            </button>
          </form>
        </div>
      </div>
      <Footer/>
    </div>
  );
};

export default ForgotPassword;
